/**
 * The live preview of the client-review header (audit 2026-09-03, decision
 * 12(a)). The document comes from the server, rendered by the same functions
 * that paint the client page; this component only displays it, inside a
 * sandboxed iframe, and reads out the server's contrast verdict beside it.
 *
 * Nothing here derives a colour. The header's foregrounds are computed from the
 * chosen background on the server (`deriveHeaderPalette`), and a second copy in
 * this package would be free to disagree with the page the client actually sees.
 */
import { useMemo } from 'react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useBrandingPreview } from '../../hooks/useTeams';
import type { BrandingAccentVerdict } from '../../lib/api/teams';

/** Header (48px) plus the light strip beneath it, measured on the rendered document. */
const PREVIEW_HEIGHT_PX = 96;
const DEBOUNCE_MS = 400;

interface BrandingPreviewProps {
  teamId: string | undefined;
  accent: string;
  background: string;
  hidePoweredBy: boolean;
  /** The stored logo; keys the preview so an upload or removal refreshes it. */
  logoUrl: string | null;
  sampleReviewUrl: string | null;
  /** Apply the server's darker suggestion AND save it, one click. */
  onUseSuggestion: (hex: string) => void;
  disabled: boolean;
}

function readout(verdict: BrandingAccentVerdict | null, onUse: (hex: string) => void, disabled: boolean) {
  if (!verdict) return <span>Accent: the default blue. Any colour you set is checked for contrast here.</span>;
  if (verdict.ok) {
    return <span>Accent contrast {verdict.ratio.toFixed(2)}:1, readable on a light page.</span>;
  }
  if (verdict.reason === 'invalid') return <span>Enter a hex colour like #2B5F3A.</span>;
  return (
    <span className="text-red-700">
      Accent measures {verdict.ratio.toFixed(2)}:1 and needs at least 4.5:1 to stay readable.
      {verdict.suggestion && (
        <button
          type="button"
          className="ml-2 font-medium underline"
          disabled={disabled}
          onClick={() => onUse(verdict.suggestion as string)}
        >
          <span className="text-cap-trim block">Use {verdict.suggestion}</span>
        </button>
      )}
    </span>
  );
}

export function BrandingPreview({
  teamId, accent, background, hidePoweredBy, logoUrl, sampleReviewUrl, onUseSuggestion, disabled,
}: BrandingPreviewProps) {
  const live = useMemo(() => ({ accent: accent.trim(), background: background.trim(), hidePoweredBy, logoUrl }), [accent, background, hidePoweredBy, logoUrl]);
  const params = useDebouncedValue(live, DEBOUNCE_MS);
  const preview = useBrandingPreview(teamId, params);
  // A verdict about the PREVIOUS colour beside a field the user just changed
  // reads as "it did not take" (frames 10 and 11, 2026-09-03). Between the
  // keystroke and the fresh document the readout says it is checking; the
  // frame itself keeps the previous render so it never blinks white.
  const checking =
    preview.isFetching ||
    params.accent !== live.accent ||
    params.background !== live.background ||
    params.hidePoweredBy !== live.hidePoweredBy ||
    params.logoUrl !== live.logoUrl;

  return (
    <div data-testid="branding-preview">
      <h3 className="section-title">Preview</h3>
      <p className="mt-1 text-xs text-gray-500">How your client link header will look. It updates as you type.</p>
      <div className="mt-2 overflow-hidden rounded-md border border-gray-200 bg-white">
        {preview.data ? (
          <iframe
            title="Preview of your client link header"
            sandbox=""
            srcDoc={preview.data.html}
            className="block w-full border-0"
            style={{ height: PREVIEW_HEIGHT_PX }}
          />
        ) : (
          <div className="skeleton-shimmer" style={{ height: PREVIEW_HEIGHT_PX }} aria-hidden="true" />
        )}
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs text-gray-500">
        <span data-testid="branding-contrast-readout">
          {preview.data && !checking ? readout(preview.data.accent, onUseSuggestion, disabled) : <span>Checking contrast...</span>}
        </span>
        {sampleReviewUrl ? (
          <a
            className="text-primary-600 font-medium hover:underline"
            href={sampleReviewUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="text-cap-trim block">See it on a client link</span>
          </a>
        ) : (
          <span>
            <a className="text-primary-600 font-medium hover:underline" href="/projects">Create a client link</a>
            {' '}from any project to see it live.
          </span>
        )}
      </div>
    </div>
  );
}
